import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, CreditCard, Mail, Globe } from "lucide-react";

export default function LegalPages() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("terms");

  const tabs = [
    { id: "terms", label: "Términos y Condiciones", icon: FileText },
    { id: "privacy", label: "Política de Privacidad", icon: Shield },
    { id: "refund", label: "Política de Reembolso", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-lg">Información Legal</h1>
              <p className="text-xs text-zinc-500">TIRACHINA - Powered by CAUCHERA</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-emerald-500 text-emerald-400"
                    : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {activeTab === "terms" && <TermsContent />}
        {activeTab === "privacy" && <PrivacyContent />}
        {activeTab === "refund" && <RefundContent />}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm">
            © 2026 TIRACHINA. Todos los derechos reservados.
          </p>
          <p className="text-zinc-600 text-xs mt-2">
            Powered by CAUCHERA NEXUS - Sistema de Dropshipping Automatizado
          </p>
        </div>
      </footer>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="prose prose-invert prose-zinc max-w-none">
      <h1 className="text-2xl font-bold mb-6">Términos y Condiciones</h1>
      <p className="text-zinc-400 text-sm mb-8">Última actualización: Marzo 2026</p>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">1. Aceptación de los Términos</h2>
        <p className="text-zinc-300 leading-relaxed">
          Al acceder y utilizar TIRACHINA (tirachina.com), usted acepta estar sujeto a estos 
          Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, 
          no podrá acceder al servicio.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">2. Descripción del Servicio</h2>
        <p className="text-zinc-300 leading-relaxed">
          TIRACHINA es una plataforma de comercio electrónico que ofrece productos de diversas 
          categorías. Operamos bajo un modelo de dropshipping, donde los productos son enviados 
          directamente desde nuestros proveedores verificados.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">3. Productos y Precios</h2>
        <ul className="text-zinc-300 space-y-2">
          <li>• Los precios están sujetos a cambios sin previo aviso.</li>
          <li>• Los precios incluyen impuestos aplicables según la jurisdicción.</li>
          <li>• Las imágenes de productos son representativas y pueden variar ligeramente.</li>
          <li>• Nos reservamos el derecho de limitar cantidades por pedido.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">4. Proceso de Compra</h2>
        <p className="text-zinc-300 leading-relaxed">
          Al realizar una compra, usted declara que tiene la capacidad legal para celebrar 
          contratos vinculantes. El proceso de compra se completa una vez que recibimos 
          confirmación del pago.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">5. Envío y Entrega</h2>
        <ul className="text-zinc-300 space-y-2">
          <li>• Tiempo de procesamiento: 1-3 días hábiles.</li>
          <li>• Tiempo de envío: 10-20 días hábiles (envío internacional estándar).</li>
          <li>• Envío express disponible con costo adicional.</li>
          <li>• Los tiempos pueden variar según la ubicación y circunstancias externas.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">6. Propiedad Intelectual</h2>
        <p className="text-zinc-300 leading-relaxed">
          Todo el contenido de TIRACHINA, incluyendo textos, gráficos, logotipos, imágenes y 
          software, es propiedad de TIRACHINA o sus licenciantes y está protegido por leyes 
          de propiedad intelectual.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">7. Limitación de Responsabilidad</h2>
        <p className="text-zinc-300 leading-relaxed">
          TIRACHINA no será responsable por daños indirectos, incidentales, especiales o 
          consecuentes que resulten del uso o la imposibilidad de usar nuestros servicios.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">8. Contacto</h2>
        <p className="text-zinc-300 leading-relaxed">
          Para consultas sobre estos términos, contáctenos en: soporte@tirachina.com
        </p>
      </section>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="prose prose-invert prose-zinc max-w-none">
      <h1 className="text-2xl font-bold mb-6">Política de Privacidad</h1>
      <p className="text-zinc-400 text-sm mb-8">Última actualización: Marzo 2026</p>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">1. Información que Recopilamos</h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Recopilamos información que usted nos proporciona directamente:
        </p>
        <ul className="text-zinc-300 space-y-2">
          <li>• Información de contacto (nombre, email, teléfono)</li>
          <li>• Dirección de envío</li>
          <li>• Información de pago (procesada de forma segura por Stripe)</li>
          <li>• Historial de pedidos</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">2. Uso de la Información</h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Utilizamos su información para:
        </p>
        <ul className="text-zinc-300 space-y-2">
          <li>• Procesar y enviar sus pedidos</li>
          <li>• Comunicarnos sobre el estado de su orden</li>
          <li>• Mejorar nuestros productos y servicios</li>
          <li>• Enviar ofertas y promociones (con su consentimiento)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">3. Compartir Información</h2>
        <p className="text-zinc-300 leading-relaxed">
          No vendemos su información personal. Solo compartimos datos con:
        </p>
        <ul className="text-zinc-300 space-y-2 mt-4">
          <li>• Proveedores de envío para entregar sus pedidos</li>
          <li>• Procesadores de pago (Stripe) para transacciones seguras</li>
          <li>• Servicios de análisis para mejorar la experiencia</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">4. Cookies y Tecnologías de Seguimiento</h2>
        <p className="text-zinc-300 leading-relaxed">
          Utilizamos cookies para mejorar su experiencia de navegación, analizar el tráfico 
          del sitio y personalizar contenido. Puede configurar su navegador para rechazar cookies.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">5. Seguridad de Datos</h2>
        <p className="text-zinc-300 leading-relaxed">
          Implementamos medidas de seguridad técnicas y organizativas para proteger su 
          información personal contra acceso no autorizado, alteración o destrucción.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">6. Sus Derechos</h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Usted tiene derecho a:
        </p>
        <ul className="text-zinc-300 space-y-2">
          <li>• Acceder a su información personal</li>
          <li>• Corregir datos inexactos</li>
          <li>• Solicitar eliminación de sus datos</li>
          <li>• Oponerse al procesamiento de sus datos</li>
          <li>• Retirar su consentimiento en cualquier momento</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">7. Contacto</h2>
        <p className="text-zinc-300 leading-relaxed">
          Para ejercer sus derechos o consultas sobre privacidad: privacidad@tirachina.com
        </p>
      </section>
    </div>
  );
}

function RefundContent() {
  return (
    <div className="prose prose-invert prose-zinc max-w-none">
      <h1 className="text-2xl font-bold mb-6">Política de Reembolso</h1>
      <p className="text-zinc-400 text-sm mb-8">Última actualización: Marzo 2026</p>

      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 mb-8">
        <h3 className="text-emerald-400 font-semibold mb-2">Garantía de Satisfacción</h3>
        <p className="text-zinc-300">
          En TIRACHINA, queremos que esté 100% satisfecho con su compra. Si no lo está, 
          trabajaremos con usted para solucionarlo.
        </p>
      </div>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">1. Período de Devolución</h2>
        <p className="text-zinc-300 leading-relaxed">
          Tiene <strong>30 días</strong> desde la recepción de su pedido para solicitar 
          una devolución o reembolso.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">2. Condiciones para Devolución</h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Para ser elegible para una devolución:
        </p>
        <ul className="text-zinc-300 space-y-2">
          <li>• El producto debe estar sin usar y en su empaque original</li>
          <li>• Debe incluir todos los accesorios y documentación</li>
          <li>• Debe proporcionar prueba de compra (número de orden)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">3. Productos Dañados o Defectuosos</h2>
        <p className="text-zinc-300 leading-relaxed">
          Si recibe un producto dañado o defectuoso:
        </p>
        <ul className="text-zinc-300 space-y-2 mt-4">
          <li>• Contáctenos dentro de las 48 horas de recepción</li>
          <li>• Envíe fotos del daño/defecto</li>
          <li>• Le enviaremos un reemplazo sin costo o reembolso completo</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">4. Proceso de Reembolso</h2>
        <ol className="text-zinc-300 space-y-3">
          <li>
            <strong>Paso 1:</strong> Contáctenos en soporte@tirachina.com con su número de orden
          </li>
          <li>
            <strong>Paso 2:</strong> Nuestro equipo revisará su solicitud en 24-48 horas
          </li>
          <li>
            <strong>Paso 3:</strong> Una vez aprobado, recibirá instrucciones de devolución
          </li>
          <li>
            <strong>Paso 4:</strong> El reembolso se procesará en 5-10 días hábiles
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">5. Costos de Envío</h2>
        <ul className="text-zinc-300 space-y-2">
          <li>• <strong>Producto defectuoso:</strong> Cubrimos el costo de envío de devolución</li>
          <li>• <strong>Cambio de opinión:</strong> El cliente cubre el envío de devolución</li>
          <li>• El costo de envío original no es reembolsable</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">6. Excepciones</h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          No aceptamos devoluciones de:
        </p>
        <ul className="text-zinc-300 space-y-2">
          <li>• Productos personalizados o hechos a medida</li>
          <li>• Productos de higiene personal (por razones sanitarias)</li>
          <li>• Productos en oferta final (claramente marcados)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-emerald-400">7. Contacto para Devoluciones</h2>
        <div className="bg-zinc-800/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-5 h-5 text-emerald-400" />
            <span className="text-zinc-300">soporte@tirachina.com</span>
          </div>
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-emerald-400" />
            <span className="text-zinc-300">tirachina.com/soporte</span>
          </div>
        </div>
      </section>
    </div>
  );
}
